/**
 * 매뉴얼 단계별 안내의 화면 좌표(service-manual 「단계별 안내 플레이어」) — 문구는 `landing:site.manual.topics.<절>.steps`.
 * 값은 장면(`GUIDE_SCENE`) 픽셀이다. `act` 누르는(입력·끄는) 자리, `view` 넓은 화면에서 확대해 보여 줄 영역(대화상자 단계),
 * `mv` 너비 760px 이하 세로 무대(4:5)에서 보여 줄 영역(누르는 자리를 담고 가장자리가 글자를 가로지르지 않게 고른 영역),
 * `widths` 이 장면만 안내 기본과 다른 그림 폭. 확정 시안의 안내 데이터에서 문구를 뺀 값이다.
 */
export type GuideRect = readonly [
  x: number,
  y: number,
  width: number,
  height: number
];

export interface GuideAction {
  /** `scroll` 은 `rect` 영역 위에서 휠을 굴린다(다음 장면이 그 방향으로 굴린 화면) */
  kind: "click" | "dblclick" | "type" | "drag" | "scroll";
  rect: GuideRect;
  /** 끌어 놓는 자리(`drag`) */
  to?: GuideRect;
  /** 위로 굴리기(`scroll` — 3D 화면이면 다가가기). 없으면 아래로 */
  dir?: "up";
  /** 오른쪽 버튼으로 누르기·끌기(3D 기울이기·돌리기 등). 없으면 왼쪽 버튼 */
  button?: "right";
}

export interface GuideFrame {
  act?: GuideAction;
  view?: GuideRect;
  mv?: GuideRect;
  widths?: readonly number[];
}

export interface ManualGuide {
  /** 그림 폭(오름차순) — 파일 이름 `f<단계>-<폭>.webp` */
  widths: readonly number[];
  frames: readonly GuideFrame[];
}

export const GUIDE_SCENE = { width: 1280, height: 800 } as const;

const GUIDES = {
  "t-new": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [559.5, 42, 143.3, 44] },
        mv: [289, 0, 520, 650],
      },
      {
        act: { kind: "type", rect: [337, 235, 360, 36] },
        view: [276, 71, 728, 651],
        mv: [305, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [337, 281, 360, 50] },
        view: [276, 71, 728, 651],
        mv: [305, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [884, 642, 72, 32] },
        view: [276, 71, 728, 651],
        mv: [752, 132, 520, 650],
      },
      {
        act: { kind: "type", rect: [364, 324, 552, 36] },
        view: [316, 71, 648, 663],
        mv: [380, 2, 520, 650],
      },
      {
        act: { kind: "click", rect: [364, 402, 270, 36] },
        view: [316, 71, 648, 663],
        mv: [298, 134, 520, 650],
      },
      {
        act: { kind: "click", rect: [364, 520, 270, 36] },
        mv: [298, 146, 520, 650],
      },
      {
        act: { kind: "type", rect: [646, 402, 270, 36] },
        view: [316, 71, 648, 663],
        mv: [616, 132, 520, 650],
      },
      {
        act: { kind: "click", rect: [844, 654, 72, 32] },
        view: [316, 71, 648, 663],
        mv: [752, 132, 520, 650],
      },
      {
        act: { kind: "click", rect: [404, 324, 250, 36] },
        view: [356, 71, 568, 507],
        mv: [282, 16, 520, 650],
      },
      {
        act: { kind: "click", rect: [515.3, 529.3, 27.4, 27.4] },
        mv: [281, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [804, 498, 72, 32] },
        view: [356, 71, 568, 507],
        mv: [740, 132, 520, 650],
      },
      { view: [720, 0, 560, 350], mv: [760, 0, 520, 650] },
    ],
  },
  "t-edit": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [105, 230.5, 600, 65.8] },
        mv: [145, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [937.4, 344, 57, 34] },
        mv: [741, 32, 520, 650],
      },
      {
        act: { kind: "type", rect: [364, 507, 552, 76] },
        view: [316, 71, 648, 612],
        mv: [380, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [844, 603, 72, 32] },
        view: [316, 71, 648, 612],
        mv: [740, 59, 520, 650],
      },
      {
        view: [720, 0, 560, 350],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-series": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [601, 245.4, 75.6, 36] },
        mv: [289, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [444, 374, 250, 36] },
        view: [396, 172, 488, 456],
        mv: [328, 54, 520, 650],
      },
      {
        act: { kind: "click", rect: [555.3, 610.8, 27.4, 27.4] },
        mv: [295, 53, 520, 650],
      },
      {
        act: { kind: "type", rect: [444, 452, 392, 76] },
        view: [396, 172, 488, 456],
        mv: [344, 54, 520, 650],
      },
      {
        act: { kind: "click", rect: [764, 552, 72, 32] },
        view: [396, 172, 488, 456],
        mv: [712, 60, 520, 650],
      },
      {
        act: { kind: "click", rect: [105, 248.4, 30, 30] },
        mv: [76, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [153, 543.7, 552, 65.8] },
        mv: [169, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [942.4, 344.5, 57, 34] },
        mv: [742, 54, 520, 650],
      },
      {
        act: { kind: "type", rect: [444, 452, 392, 76] },
        view: [396, 172, 488, 456],
        mv: [356, 54, 520, 650],
      },
      {
        act: { kind: "click", rect: [764, 552, 72, 32] },
        view: [396, 172, 488, 456],
        mv: [684, 60, 520, 650],
      },
      {
        act: { kind: "click", rect: [640, 561.6, 30, 30] },
        mv: [586, 22, 520, 650],
      },
      {
        act: { kind: "type", rect: [384, 512.4, 512, 40] },
        view: [336, 139.6, 608, 520.8],
        mv: [380, 54, 520, 650],
      },
      {
        act: { kind: "click", rect: [814, 576.4, 82, 36] },
        view: [336, 139.6, 608, 520.8],
        mv: [744, 22, 520, 650],
      },
      {
        view: [720, 0, 560, 350],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-list": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "type", rect: [104, 121, 384, 36] },
        mv: [76, 0, 520, 650],
      },
      {
        act: { kind: "type", rect: [104, 121, 384, 36] },
        mv: [76, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [496, 121, 96, 36] },
        mv: [290, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [496, 217, 240, 36] },
        view: [472, 137, 288, 372],
        mv: [290, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [662, 445.5, 64, 32] },
        view: [472, 137, 288, 372],
        mv: [280, 52, 520, 650],
      },
      {
        act: { kind: "click", rect: [105, 230.5, 600, 65.8] },
        mv: [145, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [849.9, 275, 111.4, 40] },
        mv: [742, 0, 520, 650],
      },
      {
        view: [760, 270, 504, 530],
        mv: [738, 132, 520, 650],
      },
    ],
  },
  "t-member": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 744, 40, 40] },
        mv: [0, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [228, 176, 163, 36] },
        mv: [12, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [855.7, 156.5, 87.6, 28] },
        mv: [740, 122, 520, 650],
      },
      {
        act: { kind: "type", rect: [510, 385.5, 260, 32] },
        view: [466, 294.5, 348, 211],
        mv: [380, 34, 520, 650],
      },
      {
        act: { kind: "click", rect: [706, 437.5, 64, 28] },
        view: [466, 294.5, 348, 211],
        mv: [382, 132, 520, 650],
      },
      {
        act: { kind: "click", rect: [836, 435, 72, 24] },
        mv: [740, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [980, 689.5, 64, 28] },
        mv: [752, 150, 520, 650],
      },
      {
        view: [410, 0, 870, 480],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-history": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 744, 40, 40] },
        mv: [0, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [228, 214, 163, 36] },
        mv: [10, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [883, 175, 148, 32] },
        view: [196, 49, 888, 702],
        mv: [563, 16, 520, 650],
      },
      {
        act: { kind: "click", rect: [883, 353, 148, 34] },
        mv: [563, 16, 520, 650],
      },
      {
        view: [196, 49, 888, 702],
        mv: [360, 118, 520, 650],
      },
    ],
  },
  "t-lang": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 671, 40, 40] },
        mv: [0, 121, 520, 650],
      },
      {
        act: { kind: "click", rect: [16, 710, 140, 37] },
        view: [0, 604, 188, 196],
        mv: [0, 147, 520, 650],
      },
      {
        act: { kind: "click", rect: [15.5, 744, 40, 40] },
        mv: [0, 150, 520, 650],
      },
      {
        view: [405, 278, 650, 302],
      },
    ],
  },
  "t-upload": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [849.9, 275, 98.8, 40] },
        mv: [741, 32, 520, 650],
      },
      {
        act: { kind: "click", rect: [860.8, 523, 53.7, 22] },
        mv: [741, 32, 520, 650],
      },
      {
        act: { kind: "click", rect: [855.1, 573.2, 65, 36] },
        mv: [741, 60, 520, 650],
      },
      {
        act: { kind: "click", rect: [784.9, 722, 205.6, 28] },
        mv: [741, 142, 520, 650],
      },
      {
        view: [720, 0, 560, 520],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-mapping": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [912.9, 455, 82.5, 28] },
        mv: [742, 60, 520, 650],
      },
      {
        act: { kind: "click", rect: [878.6, 419, 32, 32] },
        mv: [741, 20, 520, 650],
      },
      {
        act: { kind: "click", rect: [971.4, 423, 24, 24] },
        mv: [742, 60, 520, 650],
      },
      {
        act: { kind: "click", rect: [920, 56, 24, 24] },
        view: [296, 8, 688, 784],
        mv: [750, 28, 520, 650],
      },
      {
        act: { kind: "click", rect: [927.8, 383, 67.6, 28] },
        mv: [742, 60, 520, 650],
      },
      {
        act: { kind: "click", rect: [769, 559.5, 95, 42] },
        view: [376, 151, 528, 498],
        mv: [416, 54, 520, 650],
      },
      {
        view: [720, 0, 560, 430],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-download": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [1156, 729.9, 64, 28] },
        mv: [754, 142, 520, 650],
      },
      {
        act: { kind: "click", rect: [1155.5, 642.7, 64.5, 28] },
        mv: [754, 57, 520, 650],
      },
      {
        act: { kind: "click", rect: [735.5, 471.1, 64.5, 28] },
        view: [436, 260.9, 408, 278.2],
        mv: [342, 54, 520, 650],
      },
    ],
  },
  "t-layerdata": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [849.9, 275, 111.4, 40] },
        mv: [742, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [962.4, 337.5, 28, 28] },
        mv: [741, 32, 520, 650],
      },
      {
        view: [720, 0, 560, 460],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-terrain": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [961.2, 275, 128.2, 40] },
        mv: [754, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [1202, 337.5, 28, 28] },
        mv: [754, 32, 520, 650],
      },
      {
        act: { kind: "click", rect: [766, 397, 64, 28] },
        view: [406, 269, 468, 262],
        mv: [714, 34, 520, 650],
      },
      {
        act: { kind: "click", rect: [766, 453, 64, 28] },
        view: [406, 279, 468, 242],
        mv: [558, 34, 520, 650],
      },
      {
        view: [720, 0, 560, 460],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-ref": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [1089.4, 275, 98.8, 40] },
        mv: [755, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [1202, 337.5, 28, 28] },
        mv: [754, 32, 520, 650],
      },
      {
        view: [720, 0, 560, 460],
        mv: [760, 0, 520, 650],
      },
    ],
  },
  "t-2dlayer": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 291.6, 40, 40] },
        mv: [0, 58, 520, 650],
      },
      {
        act: { kind: "click", rect: [98, 123.6, 86.7, 18] },
        mv: [8, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [85, 291.6, 294, 42] },
        mv: [8, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [114, 389.6, 92.5, 18] },
        mv: [10, 90, 520, 650],
      },
      {
        act: { kind: "click", rect: [85, 448.6, 294, 42] },
        mv: [8, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [114, 546.6, 141.5, 18] },
        mv: [8, 120, 520, 650],
      },
      {
        view: [518.3, 244.5, 560, 350],
      },
      {
        act: { kind: "click", rect: [103, 247.6, 68.9, 18] },
        mv: [8, 4, 520, 650],
      },
      {
        view: [396, 169.4, 884, 423.1],
      },
    ],
  },
  "t-folder": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 291.6, 40, 40] },
        mv: [0, 58, 520, 650],
      },
      {
        act: { kind: "click", rect: [85, 291.6, 294, 42] },
        mv: [8, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [306, 341.6, 28, 28] },
        mv: [8, 56, 520, 650],
      },
      {
        act: { kind: "type", rect: [151, 386.6, 154, 24] },
        mv: [10, 90, 520, 650],
      },
      {
        act: { kind: "click", rect: [96, 477.6, 272, 42] },
        mv: [8, 90, 520, 650],
      },
      {
        act: { kind: "click", rect: [96, 712.6, 272, 42] },
        mv: [8, 147, 520, 650],
      },
      {
        act: { kind: "drag", rect: [96, 512.6, 272, 42], to: [151, 424.6, 56.1, 18] },
        mv: [8, 118, 520, 650],
      },
      {
        act: { kind: "click", rect: [103, 423.6, 20, 20] },
        mv: [10, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [307, 426.6, 26, 14] },
        mv: [10, 90, 520, 650],
      },
      {
        view: [396, 169.4, 884, 423.1],
      },
    ],
  },
  "t-compare": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [164, 12, 36, 36] },
        mv: [60, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [868.3, 11, 220, 38] },
        mv: [743, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [877.5, 65, 202, 31] },
        view: [828.3, 0, 300, 204],
        mv: [592, 16, 520, 650],
      },
      {
        act: { kind: "click", rect: [236, 12, 36, 36] },
        mv: [66, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [868.3, 393, 220, 38] },
        mv: [599, 124, 520, 650],
      },
      {
        act: { kind: "click", rect: [877.5, 447, 202, 31] },
        view: [828.3, 353, 300, 204],
        mv: [694, 124, 520, 650],
      },
      {
        act: { kind: "drag", rect: [362, 178.5, 24, 24], to: [202, 208.5, 24, 24] },
        mv: [12, 146, 520, 650],
      },
      {},
    ],
  },
  "t-issue": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [82, 84, 36, 36] },
        mv: [0, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [502.6, 416.7, 24, 24] },
        mv: [305, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [448, 84, 36, 36] },
        view: [394, 0, 886, 498.7],
        mv: [66, 18, 520, 650],
      },
      {
        act: { kind: "drag", rect: [662.6, 416.7, 24, 24], to: [748.6, 420.7, 24, 24] },
        mv: [479, 108, 520, 650],
      },
      {
        view: [460.6, 242.7, 600, 375],
        mv: [699, 0, 520, 650],
      },
    ],
  },
  "t-issueinfo": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 343.6, 40, 40] },
        mv: [0, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [237, 137.4, 65, 28] },
        mv: [68, 80, 520, 650],
      },
      {
        act: { kind: "click", rect: [416, 225, 66.3, 28] },
        view: [388, 115, 324, 630.9],
        mv: [84, 80, 520, 650],
      },
      {
        act: { kind: "click", rect: [620, 697.9, 64, 36] },
        view: [388, 115, 324, 630.9],
        mv: [246, 118, 520, 650],
      },
      {
        act: { kind: "click", rect: [73, 440.4, 318, 39] },
        mv: [8, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [359, 497.4, 16, 16] },
        mv: [244, 80, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [73, 41, 318, 686] },
        mv: [0, 59, 520, 650],
      },
      {
        act: { kind: "click", rect: [236, 674.9, 147, 36] },
        mv: [86, 147, 520, 650],
      },
      {
        act: { kind: "type", rect: [410, 692, 344, 40] },
        view: [386, 128, 392, 672],
        mv: [262, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [709, 744, 45, 28] },
        view: [386, 128, 392, 672],
        mv: [262, 150, 520, 650],
      },
      {
        view: [386, 128, 392, 672],
        mv: [260, 148, 520, 650],
      },
    ],
  },
  "t-measure": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [294.5, 348.1, 194.5, 71.7] },
        mv: [8, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [232, 120.4, 159, 48] },
        mv: [184, 20, 520, 650],
      },
      {
        act: { kind: "click", rect: [85, 188.4, 294, 38] },
        view: [65, 172.4, 334, 314],
        mv: [8, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [85, 268.5, 294, 34.5] },
        view: [69, 172.4, 326, 154.6],
        mv: [8, 56, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [81, 298.4, 302, 445.6] },
        view: [65, 282.4, 334, 517.6],
        mv: [8, 122, 520, 650],
      },
      {
        act: { kind: "type", rect: [81, 623.4, 222.4, 32] },
        view: [57, 568.4, 350, 110],
        mv: [8, 58, 520, 650],
      },
      {
        act: { kind: "click", rect: [313.4, 624.4, 69.6, 30] },
        view: [57, 568.4, 350, 110],
        mv: [190, 91, 520, 650],
      },
      {
        view: [65, 231.4, 334, 568.6],
        mv: [8, 62, 520, 650],
      },
      {
        act: { kind: "click", rect: [848.9, 312.2, 378.2, 159.7] },
        mv: [738, 68, 520, 650],
      },
      {
        act: { kind: "click", rect: [232, 120.4, 159, 48] },
        mv: [8, 50, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [81, 298.4, 302, 172] },
        view: [65, 282.4, 334, 204],
        mv: [8, 50, 520, 650],
      },
      {
        act: { kind: "click", rect: [73, 650.5, 318, 36] },
        view: [73, 450.5, 318, 300],
        mv: [8, 65, 520, 650],
      },
      {
        view: [69, 129.5, 326, 545],
        mv: [8, 31, 520, 650],
      },
    ],
  },
  "t-issueexport": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [831.2, 418.6, 28, 36] },
        mv: [702, 74, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [73, 41, 318, 686] },
        mv: [8, 59, 520, 650],
      },
      {
        act: { kind: "click", rect: [81, 674.9, 147, 36] },
        mv: [8, 89, 520, 650],
      },
      {
        act: { kind: "click", rect: [243, 492.1, 334, 33] },
        view: [212, 25.5, 856, 749],
        mv: [87, 104, 520, 650],
      },
      {
        act: { kind: "click", rect: [600, 430.1, 64, 30] },
        view: [212, 25.5, 856, 749],
        mv: [530, 122, 520, 650],
      },
      {
        act: { kind: "click", rect: [980, 714.5, 64, 36] },
        view: [212, 25.5, 856, 749],
        mv: [580, 149, 520, 650],
      },
      {
        view: [720, 0, 560, 350],
        mv: [760, 0, 520, 650],
      },
      {
        view: [61, 99.4, 342, 255.5],
      },
    ],
  },
  "t-droneimg": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [128, 190, 36, 36] },
        mv: [88, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [799.6, 391.9, 24, 24] },
        mv: [738, 68, 520, 650],
      },
      {
        act: { kind: "click", rect: [496, 671.5, 120, 112] },
        mv: [374, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [990, 494, 34, 34] },
        mv: [760, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [673.5, 691, 34, 34] },
        mv: [454, 149, 520, 650],
      },
      {},
    ],
  },
  "t-geolog": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 395.6, 40, 40] },
        mv: [0, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [287, 134.2, 28, 26] },
        mv: [0, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [450, 285.8, 120, 127.6] },
        view: [396, 194.8, 488, 410.4],
        mv: [298, 58, 520, 650],
      },
      {
        act: { kind: "click", rect: [217.9, 134.2, 67.1, 26] },
        view: [73, 120.2, 318, 243.2],
        mv: [0, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [440, 362.7, 195, 127.6] },
        view: [396, 269.7, 488, 260.6],
        mv: [283, 58, 520, 650],
      },
      {
        act: { kind: "click", rect: [437, 414, 406, 57] },
        view: [396, 175, 488, 450],
        mv: [385, 118, 520, 650],
      },
      {
        act: { kind: "click", rect: [745.3, 553, 94.7, 28] },
        view: [396, 175, 488, 450],
        mv: [708, 74, 520, 650],
      },
      {
        act: { kind: "click", rect: [888.3, 404.2, 24, 24] },
        mv: [743, 70, 520, 650],
      },
      {
        act: { kind: "click", rect: [1005.3, 343.2, 26, 26] },
        view: [610.3, 80.6, 580, 375.7],
        mv: [738, 68, 520, 650],
      },
      {
        view: [244, 159, 792, 482],
      },
    ],
  },
  "t-report": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 447.6, 40, 40] },
        mv: [0, 120, 520, 650],
      },
      {
        act: { kind: "type", rect: [94, 159.6, 276, 32] },
        view: [73, 40, 480, 300],
        mv: [0, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [94, 535.5, 276, 36] },
        view: [73, 100, 640, 500],
        mv: [66, 80, 520, 650],
      },
      {
        act: { kind: "click", rect: [852.5, 31, 120.3, 37] },
        mv: [509, 0, 520, 650],
      },
    ],
  },
  "t-capture": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [616, 418, 24, 24] },
        mv: [158, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [852, 730, 120, 22] },
        view: [680, 405, 600, 375],
        mv: [748, 130, 520, 650],
      },
      {
        act: { kind: "click", rect: [857, 635, 110, 30] },
        view: [680, 405, 600, 375],
        mv: [747, 69, 520, 650],
      },
      {
        view: [680, 405, 600, 375],
        mv: [748, 124, 520, 650],
      },
      {
        act: { kind: "click", rect: [128, 226, 36, 36] },
        mv: [88, 120, 520, 650],
      },
      {
        view: [72, 0, 640, 400],
        mv: [12, 26, 520, 650],
      },
      {
        act: { kind: "drag", rect: [459.9, 299.4, 24, 24], to: [808.8, 496.1, 24, 24] },
        mv: [367, 74, 520, 650],
      },
      {
        act: { kind: "click", rect: [255, 184.6, 74, 32] },
        view: [72, 48, 520, 432.2],
        mv: [90, 74, 520, 650],
      },
      {
        act: { kind: "click", rect: [165, 224.6, 164, 32] },
        view: [72, 48, 520, 432.2],
        mv: [90, 74, 520, 650],
      },
    ],
  },
  "t-pf-dxf": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 291.6, 40, 40] },
        mv: [0, 90, 520, 650],
      },
      {
        act: { kind: "click", rect: [92, 354.6, 248, 24] },
        mv: [10, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [317, 442.6, 20, 20] },
        mv: [0, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [94, 255.4, 276, 28] },
        mv: [0, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [895, 626, 24, 24] },
        mv: [593, 150, 520, 650],
      },
      {
        view: [560, 330, 720, 450],
        mv: [578, 150, 520, 650],
      },
      {
        act: { kind: "type", rect: [235, 390.3, 135, 32.1] },
        mv: [10, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [234, 568.3, 136, 28] },
        mv: [88, 116, 520, 650],
      },
      {
        act: { kind: "click", rect: [636.6, 456.5, 26, 26] },
        mv: [277, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [835, 374, 24, 24] },
        mv: [631, 2, 520, 650],
      },
      {
        act: { kind: "click", rect: [94, 568.3, 134, 28] },
        mv: [8, 116, 520, 650],
      },
      {
        view: [760, 0, 520, 325],
      },
      {
        act: { kind: "click", rect: [104, 361.4, 200, 32] },
        view: [40, 250, 480, 300],
        mv: [8, 6, 520, 650],
      },
      {
        act: { kind: "type", rect: [110, 635.1, 206, 28] },
        view: [40, 250, 480, 450],
        mv: [8, 116, 520, 650],
      },
      {
        act: { kind: "click", rect: [324, 635.1, 28, 28] },
        view: [40, 250, 480, 450],
        mv: [8, 116, 520, 650],
      },
      {
        view: [760, 0, 520, 325],
      },
      {
        act: { kind: "click", rect: [113, 548.9, 210, 28] },
        view: [40, 250, 480, 450],
        mv: [8, 56, 520, 650],
      },
      {},
    ],
  },
  "t-pf-dxf3d": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 291.6, 40, 40] },
        mv: [0, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [92, 479.6, 248, 24] },
        mv: [0, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [317, 567.6, 20, 20] },
        mv: [264, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [94, 255.4, 276, 28] },
        mv: [62, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [234, 568.3, 136, 28] },
        mv: [78, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [728, 174, 24, 24] },
        mv: [266, 6, 520, 650],
      },
      {
        mv: [573, 150, 520, 650],
      },
    ],
  },
  "t-pf-export": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [270.8, 325.4, 99.2, 27] },
        mv: [0, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [304, 365.4, 24, 24] },
        view: [40, 250, 480, 300],
        mv: [10, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [236, 339.1, 142, 32] },
        view: [204, 118.5, 872, 563],
        mv: [8, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [236, 445, 142, 33] },
        view: [204, 118.5, 872, 563],
        mv: [8, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [980, 343.1, 64, 28] },
        view: [204, 118.5, 872, 563],
        mv: [684, 0, 520, 650],
      },
      {
        view: [760, 0, 520, 325],
      },
      {
        act: { kind: "click", rect: [1000, 166.3, 36, 36] },
        view: [204, 118.5, 872, 563],
        mv: [684, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [304, 365.4, 24, 24] },
        view: [40, 250, 480, 300],
        mv: [10, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [236, 515.1, 24, 24] },
        view: [204, 118.5, 872, 563],
        mv: [8, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [949.5, 427.1, 86.5, 28] },
        view: [204, 118.5, 872, 563],
        mv: [740, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [842, 477, 188, 36] },
        view: [204, 118.5, 872, 563],
        mv: [678, 150, 520, 650],
      },
      {
        view: [760, 0, 520, 325],
        mv: [760, 4, 520, 650],
      },
    ],
  },
  "t-pf-issue": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [854.9, 355.7, 185, 83] },
        mv: [574, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [232, 120.4, 159, 48] },
        mv: [0, 54, 520, 650],
      },
      {
        act: { kind: "click", rect: [94, 655, 276, 28] },
        mv: [0, 81, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [73, 486.4, 318, 260] },
        mv: [12, 125, 520, 650],
      },
      {
        act: { kind: "click", rect: [94, 578.5, 276, 28] },
        mv: [12, 124, 520, 650],
      },
      {
        mv: [564, 150, 520, 650],
      },
    ],
  },
  "t-pf-issue3d": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 343.6, 40, 40] },
        mv: [0, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [73, 401.4, 318, 39] },
        mv: [0, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [359, 458.4, 16, 16] },
        mv: [66, 118, 520, 650],
      },
      {
        act: { kind: "drag", rect: [984, 408, 24, 24], to: [984, 258, 24, 24], button: "right" },
        mv: [730, 134, 520, 650],
      },
      {
        act: { kind: "click", rect: [232, 120.4, 159, 48] },
        mv: [0, 86, 520, 650],
      },
      {
        act: { kind: "click", rect: [94, 655, 276, 28] },
        mv: [0, 87, 520, 650],
      },
      {
        mv: [575, 150, 520, 650],
      },
    ],
  },
  "t-pf-3d": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [128, 192, 36, 36] },
        mv: [60, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [554, 731, 24, 24] },
        mv: [188, 143, 520, 650],
      },
      {
        act: { kind: "click", rect: [628, 413, 24, 24] },
        mv: [186, 120, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [614, 253, 24, 24] },
        mv: [186, 120, 520, 650],
      },
      {},
    ],
  },
  "t-pf-view": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [320.6, 456.5, 83.9, 26] },
        mv: [12, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [288.6, 459.5, 20, 20] },
        mv: [10, 8, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [567, 650, 24, 24], dir: "up" },
        mv: [527, 150, 520, 650],
      },
      {
        act: { kind: "drag", rect: [567, 694, 24, 24], to: [307, 694, 24, 24] },
        mv: [231, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [1214, 456.5, 26, 26] },
        mv: [748, 144, 520, 650],
      },
      {
        act: { kind: "click", rect: [1205, 414, 26, 26] },
        mv: [747, 102, 520, 650],
      },
      {
        act: { kind: "drag", rect: [651, 441.5, 50, 9], to: [664, 264, 24, 24] },
        mv: [555, 54, 520, 650],
      },
      {
        act: { kind: "click", rect: [1214, 286.5, 26, 26] },
        mv: [750, 2, 520, 650],
      },
      {
        act: { kind: "drag", rect: [1038, 252, 10, 10], to: [1081, 252, 10, 10] },
        view: [783, 126, 400, 250],
        mv: [757, 2, 520, 650],
      },
      {
        act: { kind: "drag", rect: [1080.6, 252, 10, 10], to: [1038, 252, 10, 10] },
        mv: [757, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [1173, 244, 26, 26] },
        mv: [757, 2, 520, 650],
      },
      {
        act: { kind: "click", rect: [1228, 342, 26, 26] },
        mv: [760, 2, 520, 650],
      },
      {
        act: { kind: "click", rect: [692, 166, 24, 24] },
        mv: [470, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [81, 725, 130, 22] },
        mv: [2, 147, 520, 650],
      },
      {
        act: { kind: "click", rect: [81, 664, 130, 24.5] },
        mv: [2, 147, 520, 650],
      },
      {
        act: { kind: "click", rect: [489.3, 725, 22, 22] },
        mv: [178, 149, 520, 650],
      },
      {
        view: [49.3, 450, 560, 350],
        mv: [38, 142, 520, 650],
      },
    ],
  },
  "t-pf-layers": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [148, 512, 77.8, 26] },
        mv: [8, 42, 520, 650],
      },
      {
        act: { kind: "click", rect: [160, 554, 208, 32] },
        mv: [8, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [326, 592, 30, 18] },
        mv: [8, 146, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [148, 542, 232, 174] },
        mv: [8, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [160, 588, 208, 32] },
        mv: [8, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [326, 626, 30, 18] },
        mv: [8, 42, 520, 650],
      },
      {
        act: { kind: "click", rect: [326, 656, 30, 18] },
        mv: [8, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [854.2, 456.5, 44, 26] },
        mv: [637, 92, 520, 650],
      },
      {
        mv: [479, 150, 520, 650],
      },
    ],
  },
  "t-pf-measure": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [584.1, 456.5, 26, 26] },
        mv: [297, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [565, 542, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [297, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [590, 738, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [298, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [508, 592, 24, 24], button: "right" },
        view: [400, 400, 640, 400],
        mv: [298, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [558.1, 456.5, 26, 26] },
        view: [400, 400, 640, 400],
        mv: [297, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [808, 736, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [650, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [532.1, 456.5, 26, 26] },
        view: [400, 400, 640, 400],
        mv: [223, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [636.1, 456.5, 26, 26] },
        view: [400, 400, 640, 400],
        mv: [427, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [707, 649, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [649, 149, 520, 650],
      },
      {
        act: { kind: "click", rect: [779, 740, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [649, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [662.1, 456.5, 26, 26] },
        view: [400, 400, 640, 400],
        mv: [427, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [642, 681, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [428, 149, 520, 650],
      },
      {
        act: { kind: "click", rect: [642, 646, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [428, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [610.1, 456.5, 26, 26] },
        view: [400, 400, 640, 400],
        mv: [427, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [957, 582, 24, 24] },
        view: [400, 400, 640, 400],
        mv: [639, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [688.1, 456.5, 26, 26] },
        view: [400, 400, 640, 400],
        mv: [427, 146, 520, 650],
      },
      {
        act: { kind: "click", rect: [667, 414.8, 68.9, 28.8] },
        view: [400, 400, 640, 400],
        mv: [426, 146, 520, 650],
      },
      {
        view: [400, 400, 640, 400],
      },
    ],
  },
  "t-pf-image": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [920.2, 456.5, 82, 26] },
        mv: [637, 130, 520, 650],
      },
      {
        act: { kind: "click", rect: [930, 329, 62, 28.8] },
        view: [560, 300, 720, 450],
        mv: [638, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [1008.2, 456.5, 84.8, 26] },
        view: [560, 300, 720, 450],
        mv: [637, 130, 520, 650],
      },
      {
        act: { kind: "type", rect: [992.6, 364, 52, 24] },
        view: [560, 300, 720, 450],
        mv: [637, 92, 520, 650],
      },
      {
        act: { kind: "click", rect: [1021, 405, 60, 28.8] },
        view: [560, 300, 720, 450],
        mv: [637, 92, 520, 650],
      },
      {
        act: { kind: "click", rect: [745.1, 491.9, 64, 28] },
        view: [426.9, 240.1, 426.2, 319.7],
        mv: [462, 150, 520, 650],
      },
    ],
  },
  "t-pf-qty": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [1099, 456.5, 62, 26] },
        mv: [759, 144, 520, 650],
      },
      {
        act: { kind: "click", rect: [442, 261.4, 107.9, 28] },
        view: [174, 156.4, 932, 487.2],
        mv: [198, 62, 520, 650],
      },
    ],
  },
  "t-camera": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [19.1, 149, 32.7, 38] },
        mv: [0, 4, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [678, 360, 24, 24], dir: "up" },
        mv: [430, 54, 520, 650],
      },
      {
        act: { kind: "drag", rect: [678, 418, 24, 24], to: [678, 338, 24, 24], button: "right" },
        mv: [592, 64, 520, 650],
      },
      {
        act: { kind: "drag", rect: [708, 418, 24, 24], to: [648, 418, 24, 24], button: "right" },
        mv: [598, 108, 520, 650],
      },
      {
        act: { kind: "drag", rect: [747, 422, 24, 24], to: [637, 452, 24, 24] },
        mv: [365, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [82, 371, 36, 36] },
        mv: [4, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [134, 405, 20, 20] },
        view: [16, 265, 334, 248],
        mv: [62, 90, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [516, 379, 24, 24] },
        mv: [264, 66, 520, 650],
      },
      {
        act: { kind: "click", rect: [1191.8, 13.2, 68.3, 43.7] },
        mv: [760, 0, 520, 650],
      },
      {},
    ],
  },
  "t-fly": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [1127.9, 497, 132.6, 34] },
        mv: [760, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [787.8, 459.7, 56.5, 27.2] },
        view: [449, 219.1, 454, 324.8],
        mv: [416, 57, 520, 650],
      },
      {
        act: { kind: "click", rect: [787.8, 459.7, 56.5, 27.2] },
        view: [449, 219.1, 454, 324.8],
        mv: [416, 57, 520, 650],
      },
      {
        act: { kind: "click", rect: [787.8, 459.7, 56.5, 27.2] },
        view: [449, 219.1, 454, 324.8],
        mv: [416, 57, 520, 650],
      },
      {},
      {
        act: { kind: "click", rect: [628, 408, 24, 24], button: "right" },
        mv: [240, 118, 520, 650],
      },
      {},
    ],
  },
  "t-3dlayer": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 291.6, 40, 40] },
        mv: [0, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [98, 175.6, 61.1, 18] },
        mv: [6, 6, 520, 650],
      },
      {
        view: [0, 104.6, 900, 563],
      },
      {
        act: { kind: "click", rect: [98, 175.6, 61.1, 18] },
        mv: [68, 4, 520, 650],
      },
      {
        act: { kind: "drag", rect: [136.4, 266.6, 12, 12], to: [295, 261, 24, 24] },
        mv: [94, 4, 520, 650],
      },
      {
        act: { kind: "drag", rect: [298.7, 266.6, 12, 12], to: [126, 261, 24, 24] },
        mv: [78, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [98, 123.6, 86.7, 18] },
        mv: [6, 6, 520, 650],
      },
      {
        view: [446, 67, 801, 501],
      },
      {
        act: { kind: "click", rect: [85, 470.6, 294, 42] },
        mv: [8, 90, 520, 650],
      },
      {
        act: { kind: "click", rect: [114, 568.6, 141.5, 18] },
        mv: [80, 120, 520, 650],
      },
      {
        view: [446, 67, 801, 501],
      },
      {
        act: { kind: "click", rect: [352, 229.6, 16, 16] },
        mv: [192, 4, 520, 650],
      },
      {
        mv: [575, 0, 520, 650],
      },
    ],
  },
  "t-measure3d": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [128, 84, 36, 36] },
        mv: [6, 30, 520, 650],
      },
      {
        act: { kind: "click", rect: [734, 408, 24, 24] },
        mv: [538, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [662, 298, 24, 24] },
        mv: [512, 0, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [671, 198, 24, 24] },
        mv: [513, 0, 520, 650],
      },
      {
        view: [433, 153, 520, 325],
      },
      {
        act: { kind: "click", rect: [128, 120, 36, 36] },
        mv: [6, 30, 520, 650],
      },
      {
        act: { kind: "click", rect: [948, 528, 24, 24] },
        mv: [518, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [708, 528, 24, 24] },
        mv: [472, 4, 520, 650],
      },
      {
        view: [598, 395, 520, 325],
      },
    ],
  },
  "t-area3d": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [128, 156, 36, 36] },
        mv: [60, 118, 520, 650],
      },
      {
        act: { kind: "click", rect: [411, 259, 24, 24] },
        mv: [60, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [381, 346, 24, 24] },
        mv: [60, 118, 520, 650],
      },
      {
        act: { kind: "click", rect: [755, 353, 24, 24] },
        mv: [333, 122, 520, 650],
      },
      {
        act: { kind: "click", rect: [905, 330, 24, 24] },
        mv: [701, 122, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [877, 241, 24, 24] },
        mv: [701, 122, 520, 650],
      },
      {
        view: [313, 95, 684, 427],
      },
      {
        act: { kind: "click", rect: [128, 192, 36, 36] },
        mv: [58, 118, 520, 650],
      },
      {
        act: { kind: "click", rect: [731, 564, 24, 24] },
        mv: [555, 122, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [683, 208, 24, 24] },
        mv: [361, 122, 520, 650],
      },
      {
        view: [72, 400, 1208, 400],
      },
      {
        act: { kind: "click", rect: [128, 228, 36, 36] },
        mv: [0, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [668, 303, 24, 24] },
        mv: [288, 0, 520, 650],
      },
      {
        view: [313, 95, 684, 427],
        mv: [291, 0, 520, 650],
      },
    ],
  },
  "t-issue3d": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [82, 84, 36, 36] },
        mv: [4, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [368, 418, 24, 24] },
        mv: [4, 0, 520, 650],
      },
      {
        view: [72, 24, 778, 486.3],
      },
      {
        act: { kind: "click", rect: [15.5, 343.6, 40, 40] },
        mv: [0, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [73, 401.4, 318, 39] },
        view: [0, 56, 800, 500],
        mv: [0, 116, 520, 650],
      },
      {
        act: { kind: "click", rect: [359, 562.4, 16, 16] },
        view: [0, 300, 800, 500],
        mv: [86, 118, 520, 650],
      },
      {
        act: { kind: "click", rect: [402, 335, 36, 36] },
        mv: [78, 24, 520, 650],
      },
      {
        view: [516, 171, 640, 400],
        mv: [444, 150, 520, 650],
      },
    ],
  },
  "t-clip": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 447.6, 40, 40] },
        mv: [0, 118, 520, 650],
      },
      {
        act: { kind: "type", rect: [283.7, 217.2, 56, 30] },
        view: [72, 40, 560, 350],
        mv: [0, 4, 520, 650],
      },
      {
        act: { kind: "type", rect: [134.3, 217.2, 110.8, 30] },
        view: [72, 40, 560, 350],
        mv: [66, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [109, 257.2, 246, 32] },
        view: [72, 40, 560, 350],
        mv: [66, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [824, 418, 24, 24] },
        view: [72, 40, 840, 525],
        mv: [480, 120, 520, 650],
      },
      {
        act: { kind: "click", rect: [322, 369.5, 34, 20.4] },
        mv: [0, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [233.5, 180.7, 119.5, 24.5] },
        mv: [66, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [109, 257.2, 246, 32] },
        view: [72, 40, 560, 350],
        mv: [66, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [824, 418, 24, 24] },
        view: [72, 40, 840, 525],
        mv: [478, 120, 520, 650],
      },
      {},
    ],
  },
  "t-clipedit": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [99, 403.7, 168, 32] },
        view: [72, 227, 560, 350],
        mv: [8, 86, 520, 650],
      },
      {
        act: { kind: "click", rect: [277, 497.7, 76, 28.5] },
        view: [72, 319, 560, 350],
        mv: [0, 86, 520, 650],
      },
      {
        act: { kind: "drag", rect: [191.2, 779.2, 14, 14], to: [107, 774, 24, 24] },
        mv: [0, 150, 520, 650],
      },
      {
        act: { kind: "drag", rect: [134.7, 627.2, 28, 28], to: [163, 629, 24, 24] },
        mv: [0, 56, 520, 650],
      },
      {
        mv: [666, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [195, 497.7, 74, 28.5] },
        view: [72, 319, 560, 350],
        mv: [0, 56, 520, 650],
      },
      {
        view: [72, 227, 560, 350],
        mv: [0, 77, 520, 650],
      },
    ],
  },
  "t-vector": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 499.6, 40, 40] },
        mv: [0, 30, 520, 650],
      },
      {
        act: { kind: "click", rect: [102, 169.7, 260, 38] },
        view: [72, 40, 560, 350],
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "type", rect: [480, 197, 292, 31.3] },
        view: [436, 135, 408, 528],
        mv: [290, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [588.7, 262.8, 102.7, 57.5] },
        view: [436, 135, 408, 528],
        mv: [290, 148, 520, 650],
      },
      {
        act: { kind: "type", rect: [480, 354.8, 320, 31.3] },
        view: [436, 135, 408, 528],
        mv: [452, 120, 520, 650],
      },
      {
        act: { kind: "type", rect: [507, 557.5, 79.3, 29.3] },
        view: [436, 135, 408, 528],
        mv: [291, 148, 520, 650],
      },
      {
        act: { kind: "click", rect: [736, 646.5, 64, 28] },
        view: [436, 135, 408, 528],
        mv: [464, 119, 520, 650],
      },
      {},
      {
        act: { kind: "click", rect: [99, 284.7, 208, 48.2] },
        view: [72, 109, 560, 350],
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [111, 343.9, 242, 34.5] },
        view: [72, 169, 560, 350],
        mv: [64, 20, 520, 650],
      },
      {
        act: { kind: "type", rect: [696, 418.8, 104, 31.3] },
        view: [436, 256, 408, 288],
        mv: [456, 42, 520, 650],
      },
      {
        act: { kind: "click", rect: [736, 472, 64, 28] },
        view: [436, 256, 408, 288],
        mv: [456, 42, 520, 650],
      },
      {
        view: [392, 400, 888, 400],
      },
    ],
  },
  "t-members": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [73, 167.4, 318, 40] },
        mv: [0, 8, 520, 650],
      },
      {
        view: [380, 322, 524, 478],
        mv: [380, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [15.5, 291.6, 40, 40] },
        mv: [0, 80, 520, 650],
      },
      {
        act: { kind: "click", rect: [89, 133.4, 286, 28] },
        mv: [0, 54, 520, 650],
      },
      {
        act: { kind: "click", rect: [757.2, 369.8, 24, 24] },
        mv: [602, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [883.3, 340.2, 24, 24] },
        mv: [602, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [903.2, 402.9, 24, 24] },
        mv: [602, 0, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [766.3, 435, 24, 24] },
        mv: [601, 150, 520, 650],
      },
      {
        act: { kind: "type", rect: [510, 400.3, 260, 30] },
        view: [462, 283.8, 356, 232.5],
        mv: [362, 100, 520, 650],
      },
      {
        act: { kind: "click", rect: [706, 444.3, 64, 28] },
        view: [462, 283.8, 356, 232.5],
        mv: [362, 100, 520, 650],
      },
      {
        view: [904, 0, 376, 146],
      },
      {
        view: [709.2, 282.2, 266, 214.8],
      },
      {
        act: { kind: "click", rect: [15.5, 239.6, 40, 40] },
        mv: [0, 8, 520, 650],
      },
      {
        view: [33, 127.4, 398, 132],
      },
    ],
  },
  "t-equip": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [15.5, 343.6, 40, 40] },
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [89, 133.4, 114, 28] },
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [786, 68, 100, 96] },
        mv: [398, 20, 520, 650],
      },
      {
        act: { kind: "click", rect: [840, 569, 130, 34] },
        view: [662, 164, 348, 479],
        mv: [576, 80, 520, 650],
      },
      {
        act: { kind: "click", rect: [808.6, 367.1, 24, 24] },
        mv: [387, 0, 520, 650],
      },
      {
        view: [627, 284.1, 303.6, 192],
      },
      {
        act: { kind: "click", rect: [81, 266.4, 28, 28] },
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [185.7, 314.4, 92.7, 28] },
        mv: [0, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [89, 577.4, 286, 30] },
        mv: [0, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [1102.1, 305.2, 24, 24] },
        mv: [687, 134, 520, 650],
      },
      {
        view: [637, 217.2, 577.1, 263.9],
      },
    ],
  },
  "t-route": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [81, 186.4, 28, 28] },
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [280.3, 234.4, 92.7, 28] },
        mv: [0, 104, 520, 650],
      },
      {
        act: { kind: "click", rect: [102, 426.4, 260, 30] },
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [826.8, 365.4, 24, 24] },
        mv: [387, 0, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [932.5, 351, 24, 24] },
        mv: [687, 150, 520, 650],
      },
      {
        act: { kind: "click", rect: [102, 309.4, 28, 28] },
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [81, 491.4, 28, 28] },
        mv: [0, 8, 520, 650],
      },
      {
        act: { kind: "click", rect: [280.3, 539.4, 92.7, 28] },
        mv: [0, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [102, 614.4, 28, 28] },
        mv: [0, 55, 520, 650],
      },
      {
        view: [740, 228.7, 430.1, 255.8],
      },
    ],
  },
  "t-ewzone": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [98, 318.6, 348, 28] },
        mv: [12, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [590, 393, 24, 24] },
        mv: [442, 80, 520, 650],
      },
      {
        act: { kind: "click", rect: [1152, 394, 24, 24] },
        mv: [760, 110, 520, 650],
      },
      {
        act: { kind: "click", rect: [1172, 450, 24, 24] },
        mv: [760, 136, 520, 650],
      },
      {
        act: { kind: "dblclick", rect: [553, 450, 24, 24] },
        mv: [441, 140, 520, 650],
      },
      {
        act: { kind: "click", rect: [98, 269.9, 348, 28] },
        mv: [12, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [98, 307.9, 348, 18] },
        view: [69, 177.4, 406, 175.5],
        mv: [12, 6, 520, 650],
      },
      {
        act: { kind: "click", rect: [96, 349.9, 94.3, 28] },
        view: [69, 177.4, 406, 225.5],
        mv: [14, 56, 520, 650],
      },
      {
        act: { kind: "click", rect: [107, 314.1, 330, 28] },
        view: [69, 177.4, 406, 318.7],
        mv: [12, 58, 520, 650],
      },
      {
        act: { kind: "click", rect: [388.1, 204.4, 57.9, 28] },
        view: [69, 177.4, 406, 225.5],
        mv: [226, 58, 520, 650],
      },
      {
        act: { kind: "scroll", rect: [510, 411, 732, 296] },
        view: [496, 324, 760, 391],
        mv: [616, 149, 520, 650],
      },
      {
        view: [496, 401, 760, 316],
        mv: [613, 150, 520, 650],
      },
    ],
  },
  "t-ewtable": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [98, 264.9, 348, 28] },
        mv: [12, 6, 520, 650],
      },
      {
        act: { kind: "drag", rect: [1064.7, 110, 20, 20], to: [1145.5, 112, 16, 16] },
        view: [484, 84, 784, 374],
        mv: [720, 2, 520, 650],
      },
      {
        view: [484, 84, 784, 374],
        mv: [615, 0, 520, 650],
      },
    ],
  },
  "t-linear": {
    widths: [1200, 1600, 1840, 2560],
    frames: [
      {
        act: { kind: "click", rect: [274, 137.4, 181, 28] },
        mv: [226, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [375, 269.6, 20, 20] },
        mv: [222, 4, 520, 650],
      },
      {
        act: { kind: "click", rect: [274, 687.9, 176, 28] },
        mv: [208, 140, 520, 650],
      },
      {
        act: { kind: "drag", rect: [894, 399, 24, 24], to: [894, 209, 24, 24] },
        mv: [690, 2, 520, 650],
      },
      {
        act: { kind: "drag", rect: [894, 217, 24, 24], to: [894, 117, 24, 24], button: "right" },
        mv: [692, 0, 520, 650],
      },
      {
        act: { kind: "click", rect: [809, 186, 24, 24] },
        mv: [725, 2, 520, 650],
      },
      {
        act: { kind: "click", rect: [1214, 456.5, 26, 26] },
        mv: [754, 2, 520, 650],
      },
      {
        act: { kind: "click", rect: [884, 413, 62, 26] },
        mv: [732, 2, 520, 650],
      },
      {
        view: [178, 160.4, 924, 479.2],
      },
    ],
  },
} satisfies Record<string, ManualGuide>;

export type ManualGuideKey = keyof typeof GUIDES;

export const MANUAL_GUIDES: Readonly<Record<ManualGuideKey, ManualGuide>> =
  GUIDES;
